// useChatAssistant.js
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "@/hooks/use-toast";

export function useChatAssistant({
  displayName = "John",
  fetchTasks,
  fetchMemories,
}) {
  const [messages, setMessages] = useState(() => {
    try {
      const cached = localStorage.getItem("vox.chat");
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });

  const [awaitingTaskTitle, setAwaitingTaskTitle] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem("vox.chat", JSON.stringify(messages));
    } catch {}
  }, [messages]);

  const nameForThirdPerson =
    displayName || localStorage.getItem("vox.displayName") || "John";

  const possessive = (n) => (/[sS]$/.test(n) ? `${n}'` : `${n}'s`);

  const thirdPerson = (s) => {
    let t = s || "";
    const n = nameForThirdPerson;
    t = t.replace(/\bmy\b/gi, possessive(n));
    t = t.replace(/\bi am\b/gi, `${n} is`);
    t = t.replace(/\bi'm\b/gi, `${n} is`);
    t = t.replace(/\bi have\b/gi, `${n} has`);
    t = t.replace(/\bi like\b/gi, `${n} likes`);
    t = t.replace(/\bi love\b/gi, `${n} loves`);
    t = t.replace(/\bi prefer\b/gi, `${n} prefers`);
    t = t.replace(/\bi work\b/gi, `${n} works`);
    t = t.replace(/^i\s+/i, `${n} `);
    return t.trim();
  };

  const classifyMemory = (txt) => {
    const t = (txt || "").toLowerCase();
    if (/favorite|favourite|i\s+like|i\s+love|i\s+prefer/.test(t))
      return "preference";
    if (/\bi am\b|\bi'm\b/.test(t)) return "identity";
    if (/work/.test(t)) return "work";
    if (/project/.test(t)) return "project";
    return "idea";
  };

  const extractMemoryProposition = (raw) => {
    let s = (raw || "").trim();
    s = s.replace(/^(remember|note|save|store)\s*(that\s*)?/i, "");
    s = s.replace(/\bi get to\s+/i, "i ");

    if (/\b(i|my)\b/i.test(s)) {
      return {
        content: thirdPerson(s.replace(/[.!?]$/, "")),
        category: classifyMemory(s),
        friendly: "Vox will remember that.",
      };
    }

    return null;
  };

  const sendMessage = async (text) => {
    setMessages((prev) => [...prev, { role: "user", content: text }]);

    const looksLikeMemory =
      /(my\s+favorite|i\s+like|i\s+love|i\s+prefer|\bi am\b|\bi'm\b|\bi work\b|^remember|^note|^save|^store)/i.test(
        text
      );

    if (looksLikeMemory) {
      const prop = extractMemoryProposition(text);
      if (prop) {
        try {
          await axios.post("/api/memories", {
            content: prop.content,
            category: prop.category,
          });

          toast({
            title: "Memory stored",
            description: "Vox will remember that.",
          });

          window.dispatchEvent(new CustomEvent("vox:memories-updated"));

          setMessages((prev) => [
            ...prev,
            { role: "assistant", content: prop.friendly },
          ]);
          return;
        } catch {
          toast({
            title: "Memory failed",
            description: "Vox tried to remember that, but couldn’t.",
            variant: "destructive",
          });
        }
      }
    }

    try {
      const res = await axios.post("/api/converse", { content: text });
      const reply =
        res.data?.reply ||
        res.data?.content ||
        res.data?.message ||
        "I’m here.";

      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: reply },
      ]);
    } catch {
      toast({
        title: "Chat error",
        description: "Something went wrong.",
        variant: "destructive",
      });
    }
  };

  const clearChat = () => setMessages([]);

  return useMemo(
    () => ({
      messages,
      awaitingTaskTitle,
      sendMessage,
      clearChat,
    }),
    [messages, awaitingTaskTitle]
  );
}
